import { createAction } from '@housinganywhere/safe-redux';

import { Thunk } from 'ha/myredux/types';
import { GlobalState } from 'ha/types/store';
import {
  PolygonRequest,
  PolygonResponse,
  TravelModeString,
} from 'ha/services/localyse';
import reportError from 'ha/helpers/bugsnag/reportError';
import { Coordinates } from 'ha/types/LeafletMap';
import { AlertType, AlertDismissAfter } from 'ha/modules/Alertbar/types';
import { send as sendAlert } from 'ha/modules/Alertbar/actions';
import { defineKey as T } from '@ha/intl';
import { PlacesServiceStatus, GeocoderResult } from 'ha/types/GoogleMaps';

import { Actions as ActionTypes } from '../constants';

const ERROR_MESSAGE = T('search.map.commute.load_polygon.error');

interface TravelTimeQueryParams {
  ttLat: string;
  ttLng: string;
  ttMode: string;
  ttTime: string;
  ttAddr: string;
  ttPlaceId: string;
}

interface PolygonLoaded {
  polygons: Array<Coordinates[]>;
  placeId: string;
  placeCoordinates: Coordinates;
  travelMode: TravelModeString;
  timeInSeconds: number;
  queryParams: TravelTimeQueryParams;
  addressName: string;
}

export const Actions = {
  clear: () =>
    createAction(ActionTypes.CLEAR_POLYGON, null, {
      queryParams: {
        ttLat: null,
        ttLng: null,
        ttMode: null,
        ttTime: null,
        ttAddr: null,
        ttPlaceId: null,
      },
      mergeQueryParams: true,
      replaceQueryParams: true,
    }),
};

const actions = {
  start: () => createAction(ActionTypes.LOAD_POLYGON_START),
  done: (payload: PolygonLoaded) => {
    const { queryParams, ...restParams } = payload;
    return createAction(ActionTypes.LOAD_POLYGON_DONE, restParams, {
      queryParams: {
        ...queryParams,
      },
      mergeQueryParams: true,
      replaceQueryParams: true,
    });
  },
  error: () => createAction(ActionTypes.LOAD_POLYGON_ERROR),
};

export const loadPolygon =
  (
    placeId: string,
    lat: number,
    lng: number,
    travelMode: TravelModeString,
    timeInSeconds: number,
    addressName: string,
  ): Thunk<GlobalState> =>
  (dispatch, _, { localyse }) => {
    dispatch(actions.start());

    let travelTimeModeProperties;
    switch (travelMode) {
      case 'car': {
        travelTimeModeProperties = { car: {} };
        break;
      }
      case 'bike': {
        travelTimeModeProperties = { bike: {} };
        break;
      }
      case 'walk': {
        travelTimeModeProperties = { walk: {} };
        break;
      }
      case 'transit': {
        travelTimeModeProperties = { transit: {} };
        break;
      }
      default: {
        travelTimeModeProperties = undefined;
      }
    }

    const request: PolygonRequest = {
      sources: [
        {
          id: placeId,
          lat,
          lng,
          tm: travelTimeModeProperties,
        },
      ],
      edgeWeight: 'time',
      polygon: {
        intersectionMode: 'union',
        serializer: 'geojson',
        srid: 4326,
        values: [timeInSeconds],
      },
    };

    return localyse
      .polygon(request)
      .then((response: PolygonResponse) => {
        const formattedCoords: Array<Coordinates[]> = [];
        response.data.features[0].geometry.coordinates.forEach(polygonCoors => {
          const coordinates = polygonCoors[0].map(([longitude, latitude]) => ({
            lat: latitude,
            lng: longitude,
          }));
          formattedCoords.push(coordinates);
        });

        dispatch(
          actions.done({
            polygons: formattedCoords,
            addressName,
            placeId,
            placeCoordinates: {
              lat,
              lng,
            },
            travelMode,
            timeInSeconds,
            queryParams: {
              ttLat: lat.toString(),
              ttLng: lng.toString(),
              ttMode: travelMode,
              ttTime: timeInSeconds.toString(),
              ttAddr: addressName,
              ttPlaceId: placeId,
            },
          }),
        );
      })
      .catch(error => {
        reportError('loadPolygon error', { metaData: { error } });

        dispatch(
          sendAlert({
            text: ERROR_MESSAGE,
            kind: AlertType.Danger,
            dismissAfter: AlertDismissAfter.Default,
          }),
        );

        dispatch(actions.error());
      });
  };

export const fetchCoordinatesAndLoadPolygon =
  (
    placeId: string,
    travelMode: TravelModeString,
    timeInSeconds: number,
    addressName: string,
  ): Thunk<GlobalState> =>
  (dispatch, _, { googleMapGeocoder }) => {
    googleMapGeocoder.init().then(Geocoder =>
      Geocoder.geocode(
        { placeId },
        (results: GeocoderResult[], status: PlacesServiceStatus) => {
          if (status === PlacesServiceStatus.OK && results.length) {
            const { lat, lng } = results[0].geometry.location;
            dispatch(
              loadPolygon(
                placeId,
                lat(),
                lng(),
                travelMode,
                timeInSeconds,
                addressName,
              ),
            );
          }
        },
      ),
    );
  };
